import React, { useState, useEffect } from "react";
import { InputWrapper } from "../../pages/auth/Login.style";
import Select from "../select/select";
import colors from "../../assets/colors.json";
import { AuthButton } from "../../pages/auth/Login.style";
import hostelApi from "../../services/hostel";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import ErrorService from "../../services/errorService";
import Loader from "../pageLoader/loader";

const AssignHouseComponent = ({
  getHostels,
  setOpenAssignHouseModal,
}) => {
  const [assignLoading, setAssignLoading] = useState(false);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const { admToken, user } = useSelector((state) => state.auth);
  
  const [selectedClass, setSelectedClass] = useState({
    id: "",
    name: "",
  });
  const [selectedHouse, setSelectedHouse] = useState({
    id: "",
    name: "",
  });
  const [selectedStudents, setSelectedStudents] = useState([]);
  
  const [classes, setClasses] = useState([]);
  const [houses, setHouses] = useState([]);
  const [students, setStudents] = useState([]);

  useEffect(() => {
    fetchClasses();
    fetchHouses();
  }, []);

  useEffect(() => {
    if (selectedClass.id) {
      fetchStudents(selectedClass.id);
    } else {
      setStudents([]);
      setSelectedStudents([]);
    }
  }, [selectedClass.id]);

  const fetchClasses = async () => {
    try {
      const res = await hostelApi.getClasses(admToken);
      if (res?.status && res?.statusCode === 2) {
        setClasses(res.data || []);
      } else {
        console.log("Failed to fetch classes:", res);
      }
    } catch (error) {
      console.log("Error fetching classes:", error);
    }
  };

  const fetchHouses = async () => {
    try {
      const res = await hostelApi.getHouses(admToken, user.schoolId);
      if (res?.status && res?.statusCode === 2) {
        setHouses(res.data || []);
      } else {
        console.log("Failed to fetch houses:", res);
      }
    } catch (error) {
      console.log("Error fetching houses:", error);
    }
  };

  const fetchStudents = async (classId) => {
    setStudentsLoading(true);
    try {
      const res = await hostelApi.getStudents(admToken, classId);
      if (res?.status && res?.statusCode === 2) {
        setStudents(res.data || []);
      } else {
        console.log("Failed to fetch students:", res);
        setStudents([]);
      }
    } catch (error) {
      console.log("Error fetching students:", error);
      setStudents([]);
    } finally {
      setStudentsLoading(false);
    }
  };

  const handleStudentSelection = (studentId) => {
    setSelectedStudents(prev => {
      if (prev.includes(studentId)) {
        return prev.filter(id => id !== studentId);
      } else {
        return [...prev, studentId];
      }
    });
  };

  const handleSelectAllStudents = () => {
    if (selectedStudents.length === students.length) {
      setSelectedStudents([]);
    } else {
      setSelectedStudents(students.map(student => student.studentId || student.id));
    }
  };

  const handleAssignStudents = async () => {
    if (!selectedClass.id || !selectedHouse.id || selectedStudents.length === 0) {
      toast.error("Please select a class, house, and at least one student");
      return;
    }

    try {
      setAssignLoading(true);
      const data = {
        houseId: selectedHouse.id,
        classId: selectedClass.id,
        studentId: selectedStudents, 
      };

      console.log("Sending data to endpoint:", data);

      const res = await hostelApi.allocateHouseToStudents(admToken, data);
      
      if (res?.status && res?.statusCode === 2) {
        toast.success(res.message || "Students assigned to house successfully");
        setSelectedStudents([]);
        setSelectedClass({ id: "", name: "" });
        setSelectedHouse({ id: "", name: "" });
        setStudents([]);
        setOpenAssignHouseModal(false);
      } else if (res?.status === 500 || res?.status === 400 || !res?.status) {
        ErrorService.displayErrorAlert(res);
        console.log(res);
      }
    } catch (error) {
      console.log("Error assigning students:", error);
      toast.error("Failed to assign students to house");
    } finally {
      setAssignLoading(false);
    }
  };

  return (
    <section className="h-[90vh] overflow-y-auto">
      <h1 className="text-[1.5rem] text-center border-b-[0.5px] border-gray-400 pb-5">
        Assign Students to House
      </h1>
      
      <div className="mt-8 space-y-6 pb-5">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <InputWrapper>
            <label>House</label>
            <Select
              title="Select House"
              index="house_name"
              selected={selectedHouse.id}
              thirdOption="id"
              options={houses}
              setSelected={(val) => {
                setSelectedHouse({
                  id: val.id,
                  name: val.house_name,
                });
              }}
            />
          </InputWrapper>
          <InputWrapper>
            <label>Class</label>
            <Select
              title="Select Class"
              index="name"
              selected={selectedClass.id}
              thirdOption="id"
              options={classes}
              setSelected={(val) => {
                setSelectedClass({
                  id: val.id,
                  name: val.name,
                });
              }}
            />
          </InputWrapper>
        </div>
        {studentsLoading && (
          <div className="flex justify-center items-center py-8">
            <Loader />
          </div>
        )}
        {selectedClass.id && !studentsLoading && students.length > 0 && (
          <div className="mt-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-700">
                Students in {selectedClass.name}
              </h3>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="selectAll"
                  checked={selectedStudents.length === students.length && students.length > 0}
                  onChange={handleSelectAllStudents}
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="selectAll" className="text-sm text-gray-600">
                  Select All
                </label>
              </div>
            </div>
            
            <div className="bg-white border border-gray-200 rounded-lg">
              {students.map((student, index) => {
                const studentId = student.studentId || student.id;
                return (
                  <div
                    key={studentId}
                    className="flex items-center px-4 py-3 border-b border-gray-100 hover:bg-gray-50 last:border-b-0"
                  >
                    <input
                      type="checkbox"
                      id={`student-${studentId}`}
                      checked={selectedStudents.includes(studentId)}
                      onChange={() => handleStudentSelection(studentId)}
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <label
                      htmlFor={`student-${studentId}`}
                      className="ml-3 flex-1 cursor-pointer text-sm text-gray-700"
                    >
                      {student.fullName || student.name || student.studentName || `Student ${index + 1}`}
                    </label>
                  </div>
                );
              })}
            </div>
            
            <div className="mt-4 text-sm text-gray-600">
              {selectedStudents.length} of {students.length} students selected
            </div>
          </div>
        )}

        {selectedClass.id && !studentsLoading && students.length === 0 && (
          <div className="mt-6 text-center py-8 bg-gray-50 rounded-lg">
            <p className="text-gray-500">No students found in this class</p>
          </div>
        )}

        {selectedClass.id && selectedHouse.id && selectedStudents.length > 0 && (
          <div className="mt-6">
            <AuthButton
              bg={colors.primary}
              disabled={assignLoading}
              className="!rounded-md w-full"
              onClick={handleAssignStudents}
            >
              {assignLoading ? "Assigning..." : `Assign ${selectedStudents.length} Student${selectedStudents.length > 1 ? 's' : ''} to ${selectedHouse.name}`}
            </AuthButton>
          </div>
        )}
      </div>
    </section>
  );
};

export default AssignHouseComponent;
